import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, FileDown, Printer, Pencil, Database, ChevronLeft, ChevronRight } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { CardSide } from "@/components/card/CardSide";
import { api } from "@/lib/api";
import { extractFields, withSystem } from "@/lib/card";
import { makeCalibrationTpl } from "@/lib/calibration";
import PrinterPanel from "@/components/print/PrinterPanel";
import { buildPages } from "@/lib/exporter";
import { useExporter } from "@/lib/useExporter";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Seg } from "@/components/editor/fields";

const PagePreview = ({ page, tpl, dataByKey, bleed }) => {
  const s = Math.min(560 / page.w, 520 / page.h);
  return (
    <div className="relative bg-white shadow-2xl shadow-black/60 mx-auto" style={{ width: page.w * s, height: page.h * s }} data-testid="print-page-preview">
      {page.cells.map((c, i) => (
        <div key={`${c.key}-${c.side}-${i}`} className="absolute" style={{ left: c.x * s, top: c.y * s }}>
          <CardSide tpl={tpl} side={c.side} data={dataByKey[c.key]} scale={s} bleed={bleed} />
          {bleed > 0 && <div className="absolute border border-dashed border-rose-500/80 pointer-events-none" data-testid="preview-trim-line"
            style={{ left: bleed * s, top: bleed * s, width: tpl.width * s, height: tpl.height * s }} />}
        </div>
      ))}
      {page.lines.map((l, i) => (
        <div key={`l${i}`} className="absolute bg-black" style={{
          left: Math.min(l.x1, l.x2) * s, top: Math.min(l.y1, l.y2) * s,
          width: l.y1 === l.y2 ? Math.abs(l.x2 - l.x1) * s : 1, height: l.y1 === l.y2 ? 1 : Math.abs(l.y2 - l.y1) * s,
        }} />
      ))}
    </div>
  );
};

export default function PrintPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [copies, setCopies] = useState(1);
  const [layout, setLayout] = useState("card");
  const [sides, setSides] = useState("both");
  const [marks, setMarks] = useState(true);
  const [bleed, setBleedState] = useState(+(localStorage.getItem("pvc_bleed") || 0));
  const setBleed = (v) => { localStorage.setItem("pvc_bleed", String(v)); setBleedState(+v); };
  const [pageIdx, setPageIdx] = useState(0);
  const [printers, setPrinters] = useState([]);
  const [printerId, setPrinterIdState] = useState(localStorage.getItem("pvc_printer") || "");
  const setPrinterId = (v) => { localStorage.setItem("pvc_printer", v); setPrinterIdState(v); };
  const { run: exportRun, job, stage } = useExporter();

  useEffect(() => {
    api.get(`/templates/${id}`).then((r) => { setTpl(r.data); if (r.data.double_sided === false) setSides("front"); }).catch(() => nav("/"));
    api.get(`/templates/${id}/records`).then((r) => { setRecords(r.data); setSelected(new Set(r.data.map((x) => x.id))); });
    api.get("/printers").then((r) => setPrinters(r.data));
  }, [id, nav]);

  const hasFields = useMemo(() => (tpl ? extractFields(tpl).length > 0 : false), [tpl]);
  const items = useMemo(() => {
    if (records.length) return records.filter((r) => selected.has(r.id)).map((r) => ({ key: r.id, data: withSystem(r) }));
    return Array.from({ length: Math.max(1, copies) }, (_, i) => ({ key: `copy${i}`, data: {} }));
  }, [records, selected, copies]);
  const dataByKey = useMemo(() => Object.fromEntries(items.map((i) => [i.key, i.data])), [items]);
  const calib = printers.find((p) => p.id === printerId) || null;
  const opts = { layout, sides, marks: layout === "a4" && marks, calib, bleed };
  const pages = useMemo(() => (tpl && items.length ? buildPages(tpl, items, opts) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tpl, items, layout, sides, marks, calib, bleed]);

  useEffect(() => { if (pageIdx >= pages.length) setPageIdx(0); }, [pages.length, pageIdx]);

  const run = (kind, calibration = false) => {
    const jt = calibration ? makeCalibrationTpl(tpl.width, tpl.height) : tpl;
    return exportRun({
      kind, tag: calibration ? "cal" : kind, tpl: jt,
      pages: calibration ? buildPages(jt, [{ key: "cal", data: {} }], { ...opts, bleed: 0 }) : pages,
      data: calibration ? { cal: {} } : dataByKey, bleed: calibration ? 0 : bleed,
      filename: `${jt.name.replace(/\W+/g, "_")}_${layout}.pdf`,
    });
  };

  const toggle = (rid) => setSelected((s) => { const n = new Set(s); n.has(rid) ? n.delete(rid) : n.add(rid); return n; });

  if (!tpl) return <div className="h-screen grid place-items-center"><Loader2 className="animate-spin text-amber-400" /></div>;
  const labelKey = Object.keys(records[0]?.data || {})[0];

  return (
    <div className="min-h-screen bg-slate-950">
      <AppHeader><span className="text-xs text-slate-500 font-mono hidden md:block">/ {tpl.name} / print</span></AppHeader>
      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-8 grid gap-8 lg:grid-cols-[360px_1fr]">
        <aside className="space-y-6">
          <div>
            <p className="text-[11px] font-mono text-amber-400 uppercase tracking-[0.2em]">Print & export studio</p>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-1" data-testid="print-page-title">{tpl.name}</h1>
            <div className="flex gap-2 mt-3">
              <button className="cf-btn-ghost h-8 text-xs" onClick={() => nav(`/editor/${id}`)} data-testid="print-edit-design-button"><Pencil size={13} /> Design</button>
              <button className="cf-btn-ghost h-8 text-xs" onClick={() => nav(`/data/${id}`)} data-testid="print-data-button"><Database size={13} /> Data</button>
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-4">
            <Opt label="Output layout">
              <Seg value={layout} onChange={setLayout} testId="print-layout" options={[{ value: "card", label: "PVC printer (card-size pages)" }, { value: "a4", label: "A4 sheet" }]} />
              <p className="text-[11px] text-slate-500 mt-1.5">{layout === "card" ? `Each page is exactly ${tpl.width}×${tpl.height}mm — for Evolis, Zebra, Fargo, Datacard. Duplex = front then back.` : "Cards tiled on A4. Back sheets are mirrored for duplex alignment."}</p>
            </Opt>
            <Opt label="Sides">
              <Seg value={sides} onChange={setSides} testId="print-sides" options={[{ value: "both", label: "Front + Back" }, { value: "front", label: "Front" }, { value: "back", label: "Back" }]} />
            </Opt>
            <Opt label="Bleed (edge-to-edge)">
              <Seg value={String(bleed)} onChange={setBleed} testId="print-bleed"
                options={[{ value: "0", label: "None" }, { value: "1", label: "1 mm" }, { value: "2", label: "2 mm" }, { value: "3", label: "3 mm" }]} />
              <p className="text-[11px] text-slate-500 mt-1.5">{bleed > 0
                ? `Backgrounds and edge-touching shapes/images extend ${bleed}mm past the trim (red dashed line). ${layout === "card" ? `Pages become ${(tpl.width + 2 * bleed).toFixed(1)}×${(tpl.height + 2 * bleed).toFixed(1)}mm — enable edge-to-edge / overprint in your printer driver.` : "Crop marks sit outside the bleed."}`
                : "Add 1–2 mm bleed to avoid thin white borders on edge-to-edge PVC prints."}</p>
            </Opt>
            {layout === "a4" && (
              <label className="flex items-center justify-between text-xs text-slate-300">Crop marks
                <Switch checked={marks} onCheckedChange={setMarks} data-testid="print-crop-marks-switch" />
              </label>
            )}
            {!records.length && (
              <Opt label="Copies">
                <input type="number" min={1} max={500} value={copies} onChange={(e) => setCopies(Math.max(1, +e.target.value || 1))} className="cf-input h-9 text-sm font-mono" data-testid="print-copies-input" />
                {hasFields && <p className="text-[11px] text-amber-400/80 mt-1.5">This design has data fields but no records — fields will print empty.</p>}
              </Opt>
            )}
          </div>

          {records.length > 0 && (
            <div className="rounded-xl border border-slate-800 bg-slate-900/60" data-testid="print-records-list">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                <span className="text-xs font-semibold" data-testid="print-selected-count">{selected.size} / {records.length} cards</span>
                <button className="text-[11px] text-amber-400 hover:text-amber-300" data-testid="print-select-all"
                  onClick={() => setSelected(selected.size === records.length ? new Set() : new Set(records.map((r) => r.id)))}>
                  {selected.size === records.length ? "Select none" : "Select all"}
                </button>
              </div>
              <ul className="max-h-64 overflow-y-auto p-2">
                {records.map((r, i) => (
                  <li key={r.id}>
                    <label className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-slate-800/60 cursor-pointer text-xs">
                      <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`print-record-checkbox-${i}`} />
                      <span className="font-mono text-slate-500 w-6">{i + 1}</span>
                      <span className="truncate">{String(r.data[labelKey] ?? "")}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <PrinterPanel printers={printers} setPrinters={setPrinters} printerId={printerId} setPrinterId={setPrinterId}
            onCalibrate={() => run("pdf", true)} busy={job?.kind === "cal"} />

          <div className="grid grid-cols-2 gap-3">
            <button className="cf-btn-primary h-11 text-sm" disabled={!!job || !pages.length} onClick={() => run("pdf")} data-testid="export-pdf-button">
              {job?.kind === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Download PDF
            </button>
            <button className="cf-btn-ghost h-11 text-sm" disabled={!!job || !pages.length} onClick={() => run("print")} data-testid="print-direct-button">
              {job?.kind === "print" ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />} Print
            </button>
          </div>
          {job && <p className="text-xs font-mono text-amber-300" data-testid="export-progress">Rendering {job.done}/{job.total} card faces at 300 DPI…</p>}
        </aside>

        <section className="rounded-xl border border-slate-800 bg-slate-950 studio-grid p-8 flex flex-col items-center gap-5 min-h-[640px]">
          <div className="flex items-center gap-4 text-xs font-mono text-slate-400">
            <button onClick={() => setPageIdx(Math.max(0, pageIdx - 1))} className="p-1.5 rounded hover:bg-slate-800" data-testid="preview-prev-page"><ChevronLeft size={16} /></button>
            <span data-testid="preview-page-indicator">Page {pages.length ? pageIdx + 1 : 0} / {pages.length} · {pages[pageIdx]?.cells[0]?.side || ""}</span>
            <button onClick={() => setPageIdx(Math.min(pages.length - 1, pageIdx + 1))} className="p-1.5 rounded hover:bg-slate-800" data-testid="preview-next-page"><ChevronRight size={16} /></button>
          </div>
          {pages[pageIdx] ? <PagePreview page={pages[pageIdx]} tpl={tpl} dataByKey={dataByKey} bleed={bleed} /> : <p className="text-sm text-slate-500">Nothing selected to print.</p>}
        </section>
      </main>

      {stage}
    </div>
  );
}

const Opt = ({ label, children }) => (
  <div>
    <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</p>
    {children}
  </div>
);
